import { createContext, useEffect, useState } from "react";
import Header from "./Components/Header/Header";
import Main from "./Components/Main";
import Modal from 'react-modal'
import Footer from "./Components/Footer/Footer";
import handlePushState from "./misc/handlePushState";

export const CarOrderingContext = createContext({})

export default function CarOrdering({
  homeUrl,
  lang,
  displaySecurityDeposit,
  restNonce,
  pageNumber,
  car,
  logo,
  steps,
  strings,
  paymentCardsImage,
  zipCodes,
  textInFooter,
  endpoints,
  regexp,
  locations
}) {
  Modal.setAppElement('body')

  const stepIds = steps.map((step) => step.id)
  const stepIdsWithoutCheckout = steps.filter((step) => !step.isCheckout).map((step) => step.id)

  const [checkoutStepValid, setCheckoutStepValid] = useState(false)
  const [validSteps, setValidSteps] = useState(stepIdsWithoutCheckout)
  const [errors, setErrors] = useState({})
  const [currentStep, setCurrentStep] = useState(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [interiorImage, setInteriorImage] = useState(null)
  const [order, setOrder] = useState({
    configuration: {
      car: {
        slug: car.slug,
      },
      settings: {},
    },
    price: car?.price ?? 0,
    security_deposit: car?.securityDeposit ?? 0,
  })

  const isAllRequiredStepsValid = () => {
    const step2 = steps.find((s) => s.id === 2 && ! s.isCheckout)

    // If step 2 isn't present, then all steps are valid
    if (!step2) {
      return true
    }

    const requiredSettings = step2.settings.filter((setting) => setting.required)

    if (requiredSettings.length <= 0) {
      return true
    }

    for (let i = 0; i < requiredSettings.length; i++) {
      const setting = requiredSettings[i]
      const settingValue = order.configuration.settings?.[setting.id] ?? []

      if (settingValue.length <= 0) {
        return false
      }
    }

    return true
  }

  useEffect(() => {
    let currentStepByPageNumber = steps.find((step) => step.id == pageNumber) ?? steps[0]

    if (currentStepByPageNumber.isCheckout && !isAllRequiredStepsValid()) {
      currentStepByPageNumber = steps[0]

      handlePushState(endpoints.order, steps[0].id, car.slug)
    }

    setCurrentStep(currentStepByPageNumber)
  }, [])

  useEffect(() => {
    const listener = () => {
      const pathname = window.location.pathname
      const pageNumber = new RegExp(regexp.order).exec(pathname)?.[2]
      const step = steps.find((step) => step.id == pageNumber)

      if (step) {
        setCurrentStep(step)
      }
    }

    window.addEventListener('popstate', listener)
    return () => window.removeEventListener('popstate', listener)
  }, [])

  useEffect(() => {
    let newPrice = car?.price ?? 0

    if (order?.guarantee_price > 0) {
      newPrice -= parseInt(order?.guarantee_price ?? 0)
      const guaranteePriceOutstandingDebt = parseInt(order?.guarantee_price_outstanding_debt ?? 0)

      if (typeof guaranteePriceOutstandingDebt === 'number' && !isNaN(guaranteePriceOutstandingDebt)) {
        newPrice += guaranteePriceOutstandingDebt
      }
    }

    Object.values(order.configuration.settings).forEach(setting => {
      newPrice += setting.reduce((total, option) => total + option.price, 0)
    })

    const newOrder = { ...order }
    newOrder.price = newPrice
    setOrder(newOrder)
  }, [order.configuration, order.guarantee_price, order.guarantee_price_outstanding_debt])

  useEffect(() => {
    const setAllStepsValid = () => setValidSteps(stepIds)
    const setAllStepsExcludedCheckoutValid = () => setValidSteps(stepIdsWithoutCheckout)

    if (isAllRequiredStepsValid()) {
      setAllStepsValid()
      return
    }

    setAllStepsExcludedCheckoutValid()
  }, [currentStep, order.configuration])

  useEffect(() => {
    if (order.payment_terms_accepted !== true && currentStep?.fields?.displayConsent) {
      setCheckoutStepValid(false)
      return
    }

    const checkoutStep = steps.find((step) => step.isCheckout)
    const showTradein = checkoutStep?.fields?.displayTradein ?? false
    const showRestpayment = checkoutStep?.fields?.displayRestpayment ?? false
    const showInsurance = checkoutStep?.fields?.displayInsurance ?? false
    const showServiceAgreement = checkoutStep?.fields?.displayServiceAgreement ?? false

    const requiredFields = [
      'first_name',
      'last_name',
      'address',
      'city',
      'zipcode',
      'telephone',
      'email',
      ...(showInsurance ? ['insurance'] : []),
    ]

    if (showTradein) {
      requiredFields.push('trade')
    }

    // Let's check required fields
    for (let i = 0; i < requiredFields.length; i++) {
      const val = order[requiredFields[i]]
      if (typeof val !== 'string' || val.length < 1) {
        setCheckoutStepValid(false)
        return
      }
    }

    const lk = Object.keys(locations)
    if (lk.length > 0 && !lk.includes(order.pickup_location)) {
      setCheckoutStepValid(false)
      return
    }

    if (showTradein && order?.trade === 'yes') {
      if (
        typeof order.trade_plate !== 'string' || order.trade_plate.length < 1 ||
        typeof order.trade_km !== 'string' || order.trade_km.length < 1
      ) {
        setCheckoutStepValid(false)
        return
      }
    }


    if (showRestpayment) {
      if (typeof order?.rest_payment !== 'string' || order.rest_payment.length < 1) {
        setCheckoutStepValid(false)
        return
      }
    }

    if (showInsurance && order?.insurance === 'no') {
      if (typeof order.insurer !== 'string' || order.insurer.length < 1) {
        setCheckoutStepValid(false)
        return
      }
    }

    if (showServiceAgreement) {
      if (typeof order?.service_agreement !== 'string' || order.service_agreement.length < 1) {
        setCheckoutStepValid(false)
        return
      }
    }

    setCheckoutStepValid(true)
  }, [order])


  if (currentStep === null) {
    return null
  }

  return (
    <CarOrderingContext.Provider value={{
      car,
      checkoutStepValid,
      currentStep,
      displaySecurityDeposit,
      endpoints,
      errors,
      homeUrl,
      interiorImage,
      isMenuOpen,
      lang,
      locations,
      logo,
      order,
      paymentCardsImage,
      restNonce,
      steps,
      strings,
      textInFooter,
      validSteps,
      zipCodes,

      setCurrentStep,
      setErrors,
      setInteriorImage,
      setIsMenuOpen,
      setOrder,
      setValidSteps,
    }}>
      <div id="nellemann-car-ordering">
        <Header/>
        <Main/>
        <Footer/>
      </div>
    </CarOrderingContext.Provider>
  )
}
